import { Component, inject, OnInit } from '@angular/core'
import { loadSalaryComponents } from '../../../store/salary-components/salary-components.actions'
import { CUSTOM_DEDUCTIONS, CUSTOM_EARNINGS, REIMBURSEMENTS } from '../../../store/state.constants'
import { MemoizedSelector, select, Store } from '@ngrx/store'
import { loadUserById } from '../../../store/user/user.actions'
import { selectSelectedUser } from '../../../store/user/user.reducer'
import { filter, firstValueFrom, take, tap } from 'rxjs'
import {
  addBusinessRequirements,
  addCtcValue,
  addSalary,
  clearSalaryState,
  editSalary,
  salaryRevisionActions
} from '../../../store/employee-salary/employee-salary.actions'
import {
  selectEmployeeSalary,
  selectRevisedSalary
} from '../../../store/employee-salary/employee-salary.reducers'
import { ActivatedRoute, Router } from '@angular/router'
import { CommonModule, NgSwitch } from '@angular/common'
import { StatutoryCompliancesComponent } from '../statutory-compliances/statutory-compliances.component'
import { CtcComponent } from '../ctc/ctc.component'
import { SalaryDataComponent } from '../salary-data/salary-data.component'
import { SalarySummaryComponent } from '../salary-summary/salary-summary.component'
import { User, UserRole } from '../../../store/auth/auth.state'
import { eps } from '../../../services/url.service'
import { CtcRevisionComponent } from '../ctc-revision/ctc-revision.component'
import { selectCtc, selectCtcValue } from '../../../store/employee-salary/employee-salary.selectors'
import {
  CTC,
  EmployeeSalary,
  EmployeeSalaryState,
  SalaryMode
} from '../../../store/employee-salary/employee-salary.state'
import { RevisedSalarySummaryComponent } from '../revised-salary-summary/revised-salary-summary.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { MonthlySalaryModalComponent } from '../monthly-salary-modal/monthly-salary-modal.component'
import { userPayrollActions } from '../../../store/payroll/payroll.actions'

@Component({
  selector: 'app-employee-salary-wrapper',
  imports: [
    NgSwitch,
    CommonModule,
    StatutoryCompliancesComponent,
    CtcComponent,
    SalaryDataComponent,
    SalarySummaryComponent,
    CtcRevisionComponent,
    RevisedSalarySummaryComponent
  ],
  templateUrl: './employee-salary-wrapper.component.html',
  styleUrl: './employee-salary-wrapper.component.scss'
})
export class EmployeeSalaryWrapperComponent implements OnInit {
  private store = inject(Store)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private modalService = inject(NgbModal)
  MonthlySalaryModalComponent = MonthlySalaryModalComponent
  currentMode!: SalaryMode
  salaryMode = SalaryMode
  screens = [0, 1, 2, 3]
  employeeId = ''
  belongsTo = ''
  employeeName = ''
  user: User | null = null

  //---State---//
  currentScreenIndex = 0
  nextButtonState = false
  order = 'asc'
  ctc$ = this.store.select(selectCtc)

  async ngOnInit() {
    const employeeId = this.route.snapshot.paramMap.get('id')
    if (employeeId) this.employeeId = employeeId
    else this.router.navigate([eps.hrm.employeePath, this.employeeId])

    this.currentMode = this.route.snapshot.data['mode'] as SalaryMode

    this.store
      .select(selectCtcValue)
      .pipe(take(1))
      .subscribe(ctcValue => {
        if (this.currentMode !== SalaryMode.CREATE && ctcValue <= 0) {
          this.router.navigate([eps.hrm.employeePath, this.employeeId])
        }
      })

    this.store.dispatch(loadUserById({ id: this.employeeId }))
    this.store.dispatch(
      userPayrollActions.loadLatestUserPayrollDate({ employeeId: this.employeeId })
    )

    await this.loadInitialUserDataAndDispatch()
    this.isContractor(this.user) ? (this.currentScreenIndex = 1) : (this.currentScreenIndex = 0)
  }

  async loadInitialUserDataAndDispatch(): Promise<void> {
    this.user = await firstValueFrom(
      this.store.pipe(
        select(selectSelectedUser),
        filter((user): user is User => !!user),
        take(1)
      )
    )

    if (this.user) {
      this.employeeName = this.user.firstName + ' ' + (this.user.lastName ?? '')
      this.belongsTo = this.user.business?.id ?? ''

      switch (this.currentMode) {
        case SalaryMode.CREATE:
          this.store.dispatch(
            addBusinessRequirements({
              businessRequirements: { belongsTo: this.belongsTo, employeeId: this.employeeId }
            })
          )
          break
        case SalaryMode.REVISE:
        case SalaryMode.UPDATE:
          this.store.dispatch(
            salaryRevisionActions.initiateSalaryRevision({
              belongsTo: this.belongsTo,
              employeeId: this.employeeId
            })
          )
          break
        default:
          this.router.navigate([eps.hrm.employeePath, this.employeeId])
      }

      this.store.dispatch(
        loadSalaryComponents({ componentType: CUSTOM_EARNINGS, belongsTo: this.belongsTo })
      )
      this.store.dispatch(
        loadSalaryComponents({ componentType: CUSTOM_DEDUCTIONS, belongsTo: this.belongsTo })
      )
      this.store.dispatch(
        loadSalaryComponents({ componentType: REIMBURSEMENTS, belongsTo: this.belongsTo })
      )
    }
  }

  nextScreen() {
    // When screen[1] renders the default state of nextButtonState should be true
    //if (this.currentScreenIndex === 0) this.nextButtonState = true

    if (this.currentScreenIndex < this.screens.length - 1) {
      this.currentScreenIndex++
      return
    }

    switch (this.currentMode) {
      case SalaryMode.CREATE:
        this.store
          .pipe(
            select(selectEmployeeSalary),
            take(1),
            tap(salary => {
              this.store.dispatch(addSalary({ salary }))
            })
          )
          .subscribe()
        break
      case SalaryMode.REVISE:
        this.store
          .pipe(
            select(selectRevisedSalary),
            take(1),
            tap(salary => {
              this.store.dispatch(salaryRevisionActions.saveRevisedSalary({ salary }))
            })
          )
          .subscribe()
        break
      case SalaryMode.UPDATE:
        this.store
          .pipe(
            select(selectRevisedSalary),
            take(1),
            tap(salary => {
              this.store.dispatch(editSalary({ salary }))
            })
          )
          .subscribe()
        break
      default:
        this.router.navigate([eps.hrm.employeePath, this.employeeId])
    }
    this.router.navigate([eps.hrm.employeePath, this.employeeId])
  }

  previousScreen() {
    this.nextButtonState = false
    if (this.currentScreenIndex > 0) {
      this.currentScreenIndex--
    } else {
      this.store.dispatch(clearSalaryState())
      this.router.navigate([eps.hrm.employeePath, this.employeeId])
    }
  }

  // Created to reset the screen to navigate from summary screen to compliance screen
  resetScreen(screenNumber: number) {
    this.currentScreenIndex = screenNumber
  }

  setNextButtonState(state: boolean) {
    queueMicrotask(() => {
      this.nextButtonState = state
    })
  }

  onformValuesChanged(value: CTC) {
    const ctcValueState = value.ctc !== 0 && value.ctc !== null
    const effectiveFromState = value.effectiveFrom !== '' && value.effectiveFrom !== null
    const payoutFromState = value.payoutFrom !== '' && value.payoutFrom !== null

    // Temporarily store the next button state so that changing it can be defered
    let newNextButtonState: boolean

    switch (this.currentMode) {
      case SalaryMode.CREATE:
        this.store.dispatch(addCtcValue({ ctcValue: value.ctc }))
        newNextButtonState =
          this.currentScreenIndex === 0 || (ctcValueState && this.currentScreenIndex >= 1)
            ? false
            : true
        break
      case SalaryMode.REVISE:
        this.store.dispatch(salaryRevisionActions.updateCTCValue({ ctcValue: value.ctc }))
        newNextButtonState =
          this.currentScreenIndex === 0 ||
          (ctcValueState && effectiveFromState && payoutFromState && this.currentScreenIndex >= 1)
            ? false
            : true
        break
      case SalaryMode.UPDATE:
        this.store.dispatch(salaryRevisionActions.updateCTCValue({ ctcValue: value.ctc }))
        newNextButtonState =
          this.currentScreenIndex === 0 || (ctcValueState && this.currentScreenIndex >= 1)
            ? false
            : true
        break
      default:
        this.router.navigate([eps.hrm.employeePath, this.employeeId])
    }
    queueMicrotask(() => {
      this.nextButtonState = newNextButtonState
    })
  }

  openModal(modal: unknown) {
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
    let employeeSalary!: MemoizedSelector<EmployeeSalaryState, EmployeeSalary>
    if (this.currentMode === SalaryMode.CREATE) {
      employeeSalary = selectEmployeeSalary
    } else {
      employeeSalary = selectRevisedSalary
    }
    this.store
      .select(employeeSalary)
      .pipe(
        take(1),
        tap(salary => {
          if (modalRef.componentInstance) {
            modalRef.componentInstance.employee = this.user
            modalRef.componentInstance.employeeSalaryDetails = salary
          }
        })
      )
      .subscribe()
  }

  isContractor(employee: any): boolean {
    return employee?.userRoles?.some((r: UserRole) => r?.role?.name === 'Contractor') || false
  }
}
